import { useEffect, useState } from "react";

import type {
  CreateButtonSetRequest,
  ParsedButtonSet,
  PersonalButtonSet,
  UpdateButtonSetRequest
} from "../../api/client";
import { ApiError } from "../../api/client";
import type { ButtonLayoutLimits } from "../composer/button-layout-model";
import { ButtonSetEditor, type ButtonSetEditorApi } from "./ButtonSetEditor";

export type ButtonSetsApi = ButtonSetEditorApi & {
  loadButtonSets: () => Promise<PersonalButtonSet[]>;
  parseButtonSet: (source: string) => Promise<ParsedButtonSet>;
  createButtonSet: (body: CreateButtonSetRequest) => Promise<PersonalButtonSet>;
  updateButtonSet: (id: string, body: UpdateButtonSetRequest) => Promise<PersonalButtonSet>;
  copyButtonSet: (id: string, body: { requestId: string; name: string }) => Promise<PersonalButtonSet>;
  deleteButtonSet: (id: string) => Promise<void>;
};

const buttonCount = (item: PersonalButtonSet) =>
  item.buttonRows.reduce((total, row) => total + row.buttons.length, 0);

export function ButtonSetsScreen({
  api,
  limits,
  onClose
}: {
  api: ButtonSetsApi;
  limits: ButtonLayoutLimits;
  onClose: () => void;
}) {
  const [items, setItems] = useState<PersonalButtonSet[] | null>(null);
  const [editor, setEditor] = useState<{ item: PersonalButtonSet | null; mode: "manual" | "import" } | null>(null);
  const [deleteCandidate, setDeleteCandidate] = useState<PersonalButtonSet | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void api.loadButtonSets().then(
      (value) => { if (active) setItems(value); },
      () => { if (active) { setItems([]); setError("Не удалось загрузить личные наборы"); } }
    );
    return () => { active = false; };
  }, [api.loadButtonSets]);

  if (editor) {
    return <ButtonSetEditor
      api={api}
      limits={limits}
      item={editor.item}
      initialMode={editor.mode}
      onCancel={() => setEditor(null)}
      onSaved={(saved) => {
        setItems((current) => current
          ? current.some((item) => item.id === saved.id)
            ? current.map((item) => item.id === saved.id ? saved : item)
            : [saved, ...current]
          : [saved]);
        setEditor(null);
      }}
    />;
  }

  const copy = async (item: PersonalButtonSet) => {
    setBusyId(item.id);
    setError(null);
    try {
      const copied = await api.copyButtonSet(item.id, {
        requestId: crypto.randomUUID(),
        name: `${item.name} — копия`
      });
      setItems((current) => current ? [copied, ...current] : [copied]);
    } catch {
      setError("Не удалось создать копию набора");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async () => {
    if (!deleteCandidate) return;
    setBusyId(deleteCandidate.id);
    setError(null);
    try {
      await api.deleteButtonSet(deleteCandidate.id);
      setItems((current) => current?.filter((item) => item.id !== deleteCandidate.id) ?? []);
      setDeleteCandidate(null);
    } catch (error) {
      setError(error instanceof ApiError && error.code === "BUTTON_SET_IN_USE"
        ? error.message
        : "Не удалось удалить набор");
    } finally {
      setBusyId(null);
    }
  };

  return <section className="button-sets-screen screen-stack section-screen" aria-label="Мои наборы кнопок">
    <header className="composer-header">
      <button type="button" aria-label="Закрыть мои наборы" onClick={onClose}>←</button>
      <div><p className="eyebrow">Личная библиотека</p><h1>Мои наборы кнопок</h1></div>
      <span />
    </header>
    <div className="button-set-create-actions">
      <button type="button" onClick={() => setEditor({ item: null, mode: "manual" })}>Создать набор</button>
      <button type="button" onClick={() => setEditor({ item: null, mode: "import" })}>Вставить готовый список</button>
    </div>
    {error && <p className="inline-error" role="alert">{error}</p>}
    {items === null ? <div className="skeleton list-skeleton" /> : items.length === 0 ? <div className="empty-state"><strong>Личных наборов пока нет</strong><p>Создайте кнопки вручную или вставьте готовый список из Telegram.</p></div> : <div className="button-set-list">{items.map((item) => <article aria-label={`Набор ${item.name}`} key={item.id}>
      <div><strong>{item.name}</strong><small>{item.buttonRows.length} рядов · {buttonCount(item)} кнопок</small></div>
      <div className="button-set-card-actions">
        <button type="button" aria-label={`Редактировать ${item.name}`} onClick={() => setEditor({ item, mode: "manual" })}>Редактировать</button>
        <button type="button" aria-label={`Создать копию ${item.name}`} disabled={busyId === item.id} onClick={() => void copy(item)}>Копия</button>
        <button type="button" aria-label={`Удалить ${item.name}`} disabled={busyId === item.id} onClick={() => setDeleteCandidate(item)}>Удалить</button>
      </div>
    </article>)}</div>}
    {deleteCandidate && <div className="button-set-delete-confirm" role="dialog" aria-modal="true" aria-label="Подтверждение удаления">
      <strong>Удалить набор «{deleteCandidate.name}»?</strong>
      <p>Если набор связан с публикациями, сначала отвяжите его в редакторе постов.</p>
      <div><button type="button" onClick={() => setDeleteCandidate(null)}>Отмена</button><button className="danger-action" type="button" disabled={busyId === deleteCandidate.id} onClick={() => void remove()}>Удалить набор безвозвратно</button></div>
    </div>}
  </section>;
}
